import React, { useEffect, useState, useRef, createContext, useContext } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box
} from '@mui/material'
import Layout from './components/Layout/Layout'
import Login from './pages/Login'
import CertificateOnboard from './pages/CertificateOnboard'
import AWSCertificateTable from './pages/AWSCertificateTable'
import ProductOnboard from './pages/ProductOnboard'
import OnboardedProducts from './pages/OnboardedProducts'
import BulkCertificateUpload from './pages/BulkCertificateUpload'
import CRQs from './pages/CRQs'
import Admin from './pages/Admin'
import { isAuthenticated, clearAuthCookies } from './utils/auth'
import api from './utils/api'

// Create context for grace period state
const GracePeriodContext = createContext({ inGracePeriod: false })

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { inGracePeriod } = useContext(GracePeriodContext)

  // Allow access during grace period even if token expired
  if (!isAuthenticated() && !inGracePeriod) {
    // Token is missing or expired, clear localStorage
    // Avoid calling logout when no session exists (prevents startup logout requests)
    localStorage.removeItem('authToken')
    localStorage.removeItem('username')
    localStorage.removeItem('mail')
    return <Navigate to="/login" replace />
  }
  return children
}

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const [inGracePeriod, setInGracePeriod] = useState(false)
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const [idleCountdown, setIdleCountdown] = useState(120) // 2 minutes in seconds
  const lastUserActivityRef = useRef(Date.now()) // Use ref instead of state for activity tracking

  // Track user activity (mouse movement, clicks, keyboard)
  useEffect(() => {
    const updateActivity = () => {
      lastUserActivityRef.current = Date.now()
      // Only hide warning if it's showing
      if (showIdleWarning) {
        console.log('✅ User activity detected - dismissing idle warning')
        setShowIdleWarning(false)
      }
    }

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity)
      })
    }
  }, [])

  // Check for idle timeout and show warning
  useEffect(() => {
    if (!isAuthenticated()) {
      console.log('⏸️ Idle check skipped - not authenticated')
      return
    }

    console.log('✅ Starting idle timeout checker (warning at 28 min, logout at 30 min)')

    const idleCheckInterval = setInterval(() => {
      // Double-check authentication before checking idle time
      if (!isAuthenticated()) {
        console.log('⏸️ User logged out - stopping idle checker')
        clearInterval(idleCheckInterval)
        return
      }

      const idleTime = Date.now() - lastUserActivityRef.current
      const idleMinutes = idleTime / (1000 * 60)

      // Log idle time every check for debugging
      if (idleMinutes >= 20) {
        console.log(`⏱️ User idle for ${idleMinutes.toFixed(1)} minutes`)
      }

      // Show warning at 28 minutes of inactivity
      if (idleMinutes >= 28 && !showIdleWarning) {
        console.log('⚠️ User idle for 28 minutes - showing warning dialog')
        setShowIdleWarning(true)
        setIdleCountdown(120) // Reset countdown to 2 minutes
      }

      // At 30 minutes, logout automatically
      if (idleMinutes >= 30) {
        console.log('🚪 30 minutes of inactivity - logging out automatically')
        handleIdleLogout()
      }
    }, 5000) // Check every 5 seconds

    return () => {
      console.log('🛑 Stopping idle timeout checker')
      clearInterval(idleCheckInterval)
    }
  }, []) // Run once on mount, no dependencies to avoid restarting interval

  // Countdown timer for warning dialog
  useEffect(() => {
    if (!showIdleWarning) return

    const countdownInterval = setInterval(() => {
      setIdleCountdown(prev => {
        if (prev <= 1) {
          handleIdleLogout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(countdownInterval)
  }, [showIdleWarning])

  // Conditional auto-refresh (only if user has been active recently)
  useEffect(() => {
    // Only run if authenticated (not on login page)
    if (!isAuthenticated()) {
      console.log('⏸️ Auto-refresh skipped - not authenticated')
      return
    }

    console.log('🔄 Starting auto-refresh timer (10 minutes) - Access token expires in 15 minutes')

    // Silent refresh every 10 minutes to keep session alive
    const refreshInterval = setInterval(async () => {
      // Double-check authentication before attempting refresh
      if (!isAuthenticated()) {
        console.log('⏸️ User logged out - stopping auto-refresh')
        clearInterval(refreshInterval)
        return
      }

      // Check if user has been active in the last 25 minutes (before idle warning at 28)
      const idleTime = Date.now() - lastUserActivityRef.current
      const idleMinutes = idleTime / (1000 * 60)

      if (idleMinutes >= 25) {
        console.log(`⏸️ User idle for ${Math.floor(idleMinutes)} minutes - skipping auto-refresh (idle timeout will handle logout)`)
        return // Don't refresh if user is idle - let idle timeout handle it
      }

      // Check if another tab is refreshing (shared lock via localStorage)
      const refreshLock = localStorage.getItem('refresh_lock')
      if (refreshLock) {
        const lockTime = parseInt(refreshLock)
        const now = Date.now()
        // If lock is less than 10 seconds old, skip (another tab is refreshing)
        if (now - lockTime < 10000) {
          console.log('⏸️ Another tab is refreshing, skipping...')
          return
        }
      }

      try {
        // Set lock (expires in 10 seconds)
        localStorage.setItem('refresh_lock', Date.now().toString())
        
        // This will rotate tokens automatically (backend handles it)
        console.log('⏰ Auto-refresh triggered (user is active)')
        await api.post('/auth/refresh')
        console.log('✅ Session refreshed automatically')
      } catch (error) {
        console.error('❌ Auto-refresh failed:', error)
        // Session expired - redirect to login (only if still authenticated locally)
        if (error.response?.status === 401 && isAuthenticated()) {
          console.log('Session expired - redirecting to login')
          localStorage.removeItem('username')
          localStorage.removeItem('mail')
          navigate('/login', { replace: true })
        }
      } finally {
        // Clear lock
        localStorage.removeItem('refresh_lock')
      }
    }, 10 * 60 * 1000) // Every 10 minutes (access token expires in 15)

    // Cleanup on unmount
    return () => {
      console.log('🛑 Stopping auto-refresh timer')
      clearInterval(refreshInterval)
    }
  }, [navigate]) // Don't include lastUserActivity - it would restart interval on every user action

  const handleContinueWorking = async () => {
    console.log('✅ User clicked Continue Working - triggering refresh')
    try {
      await api.post('/auth/refresh')
      setShowIdleWarning(false)
      lastUserActivityRef.current = Date.now() // Reset activity timer
      console.log('✅ Session refreshed, idle timer reset')
    } catch (error) {
      console.error('❌ Failed to refresh session:', error)
      handleIdleLogout()
    }
  }

  const handleIdleLogout = async () => {
    console.log('🚪 Logging out due to inactivity')
    setShowIdleWarning(false)
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
    localStorage.removeItem('username')
    localStorage.removeItem('mail')
    navigate('/login', { replace: true })
  }

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <GracePeriodContext.Provider value={{ inGracePeriod }}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<CertificateOnboard />} />
                  <Route path="/aws-certificates" element={<AWSCertificateTable />} />
                  <Route path="/product-dl" element={<ProductOnboard />} />
                  <Route path="/onboarded-products" element={<OnboardedProducts />} />
                  <Route path="/bulk-upload" element={<BulkCertificateUpload />} />
                  <Route path="/crqs" element={<CRQs />} />
                  <Route path="/admin" element={<Admin />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Idle Warning Dialog */}
      <Dialog
        open={showIdleWarning}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'warning.contrastText' }}>
          ⚠️ Session About to Expire
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            You've been inactive for 28 minutes. Your session will expire in:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            my: 3 
          }}>
            <Typography variant="h2" color="error" sx={{ fontWeight: 'bold' }}>
              {formatCountdown(idleCountdown)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Click "Continue Working" to stay logged in, or you'll be automatically logged out.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleIdleLogout}
            variant="outlined"
            color="inherit"
          >
            Logout Now
          </Button>
          <Button 
            onClick={handleContinueWorking}
            variant="contained"
            color="primary"
            autoFocus
          >
            Continue Working
          </Button>
        </DialogActions>
      </Dialog>
    </GracePeriodContext.Provider>
  )
}

export default App
